import { useState } from "react";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, FileText, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const inputCls = "w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent";
const labelCls = "block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1";

export function ActivityExportDialog({ open, onOpenChange, client }) {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [includeNotes, setIncludeNotes] = useState(true);
  const [includeFiles, setIncludeFiles] = useState(true);
  const [busy, setBusy] = useState(false);

  const run = async (format) => {
    if (!includeNotes && !includeFiles) { toast.error("Select at least notes or attachments"); return; }
    setBusy(true);
    try {
      const params = { format, include_notes: includeNotes, include_files: includeFiles };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await api.get(`/clients/${client.id}/export`, { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `client_${(client?.client_id || "record").toUpperCase()}_activity.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${format.toUpperCase()}`);
      onOpenChange(false);
    } catch { toast.error("Export failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-sm" data-testid="activity-export-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold tracking-tight">Export activity report</DialogTitle>
          <DialogDescription className="text-xs">Choose a date range and what to include, then pick a format.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-1">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>From</label>
              <input type="date" data-testid="export-date-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>To</label>
              <input type="date" data-testid="export-date-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className={inputCls} />
            </div>
          </div>
          <p className="text-xs text-gray-400 -mt-2">Leave dates blank to include all activity.</p>

          <div className="space-y-2">
            <label className="flex items-center gap-2.5 cursor-pointer" data-testid="export-include-notes">
              <input type="checkbox" checked={includeNotes} onChange={(e) => setIncludeNotes(e.target.checked)} className="h-4 w-4 accent-[#002FA7]" />
              <span className="text-sm text-gray-700 font-medium">Include notes &amp; activity (incl. sent emails)</span>
            </label>
            <label className="flex items-center gap-2.5 cursor-pointer" data-testid="export-include-files">
              <input type="checkbox" checked={includeFiles} onChange={(e) => setIncludeFiles(e.target.checked)} className="h-4 w-4 accent-[#002FA7]" />
              <span className="text-sm text-gray-700 font-medium">Include attachments list</span>
            </label>
          </div>
        </div>

        <DialogFooter className="pt-2">
          <button type="button" onClick={() => run("csv")} disabled={busy} data-testid="export-run-csv"
            className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-sm font-semibold text-sm flex items-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />} CSV
          </button>
          <button type="button" onClick={() => run("pdf")} disabled={busy} data-testid="export-run-pdf"
            className="bg-[#002FA7] hover:bg-[#002480] text-white px-4 py-2 rounded-sm font-semibold text-sm flex items-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} PDF
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
